import { useEffect, useState } from 'react';
import { supabase } from '@shared/lib/supabase/client';
import { useAuth } from '@shared/features/auth/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Globe, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@shared/components/ui/alert';

interface UserProfile {
  portal: 'lexlabour' | 'lexzone' | 'lexwoo';
  portal_toegang: Array<'lexlabour' | 'lexzone' | 'lexwoo'>;
  heeft_alle_portals: boolean;
}

export function PortalAccessSettings() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProfile() {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('gebruiker_profielen')
          .select('portal, portal_toegang, heeft_alle_portals')
          .eq('id', user.id)
          .single<UserProfile>();

        if (error) throw error;
        setProfile(data);
      } catch (err) {
        console.error('Failed to fetch profile:', err);
        setError('Kon portal informatie niet ophalen');
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [user]);

  const getPortalName = (portalId: string) => {
    switch (portalId) {
      case 'lexlabour':
        return 'LexLabour';
      case 'lexzone':
        return 'LexOmgevingswet';
      case 'lexwoo':
        return 'LexWoo';
      default:
        return portalId;
    }
  };

  const getPortalLogo = (portalId: string) => {
    switch (portalId) {
      case 'lexlabour':
        return '/lexlabour_logo.svg';
      case 'lexzone':
        return '/lexzone_logo.svg';
      case 'lexwoo':
        return '/lexwoo_logo.svg';
      default:
        return '';
    }
  };

  const getPortalTheme = (portalId: string) => {
    switch (portalId) {
      case 'lexlabour':
        return 'bg-muted border-amber-400 hover:bg-accent';
      case 'lexzone':
        return 'bg-muted border-green-400 hover:bg-accent';
      case 'lexwoo':
        return 'bg-muted border-blue-400 hover:bg-accent';
      default:
        return 'bg-muted border-border hover:bg-accent';
    }
  };

  const getPortalUrl = (portalId: string) => {
    switch (portalId) {
      case 'lexlabour':
        return 'https://lexlabour.nl';
      case 'lexzone':
        return 'https://lexzone.nl';
      case 'lexwoo':
        return 'https://lexwoo.nl';
      default:
        return '#';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Portal toegang
          </CardTitle>
          <CardDescription>Beheer uw toegang tot verschillende portals</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Laden...</p>
        </CardContent>
      </Card>
    );
  }

  if (error || !profile) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Portal toegang
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>{error || 'Kon portal informatie niet laden'}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Portal toegang
        </CardTitle>
        <CardDescription>
          U heeft toegang tot de volgende portals
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {profile.heeft_alle_portals && (
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>
              U heeft toegang tot alle portals (admin account)
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">Actieve portals</h3>
          <div className="space-y-2">
            {profile.portal_toegang.map((portal) => (
              <a
                key={portal}
                href={getPortalUrl(portal)}
                target="_blank"
                rel="noopener noreferrer"
                className={`${getPortalTheme(portal)} border-2 rounded-lg px-4 py-3 flex items-center gap-3 transition-colors cursor-pointer`}
              >
                <img
                  src={getPortalLogo(portal)}
                  alt={getPortalName(portal)}
                  className="h-8 w-16 object-contain"
                />
                <span className="text-base font-serif text-foreground">
                  {getPortalName(portal)}
                </span>
              </a>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            Om toegang tot een ander portal aan te vragen, log in op dat portal met uw bestaande account.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
